import { DocumentViewer } from '@aecom-ai-engineering/react-components';
import { Button, LoadingArea, Spinner } from '@consigli/facade';
import { useBlobs, useDownloadFilesAsZip, usePackageId, useProjectId } from '@consigli/hooks';
import {
  type Blob,
  BlobParsedStatus,
  convertBlobToDocument,
  VersionCompareRole,
} from '@consigli/types';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineFileDownload } from 'react-icons/md';

import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { createRenderers } from '@/features/document-viewer/renderers';
import { WorkspaceProvider } from '@/layouts/workspace-layout/use-workspace-context';
import { WorkspaceLayout } from '@/layouts/workspace-layout/workspace-layout';
import { resolveDocumentFileType, resolveDocumentSrc } from '@/util/document-check';
import { snapCenterToCursor } from '@/util/dragging-util';

enum DocumentFilterTab {
  ALL = 'all',
  VERSION_A = 'version-a',
  VERSION_B = 'version-b',
  COMMENT = 'comment',
}

interface VersionCompareDocumentRow {
  name: string;
  primaryBlob?: Blob;
  secondaryBlob?: Blob;
  commentBlob?: Blob;
}

const buildRows = (blobs: Blob[]): VersionCompareDocumentRow[] => {
  return blobs.map((blob) => {
    const role = blob.metadata?.versionCompareRole;
    return {
      name: blob.name,
      primaryBlob: role === VersionCompareRole.PRIMARY_VERSION ? blob : undefined,
      secondaryBlob: role === VersionCompareRole.SECONDARY_VERSION ? blob : undefined,
      commentBlob: role === VersionCompareRole.COMMENT_VERSION ? blob : undefined,
    };
  });
};

const VersionCompareDocumentsContent: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const [activeTab, setActiveTab] = useState<DocumentFilterTab>(DocumentFilterTab.ALL);
  const [activeBlobId, setActiveBlobId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor),
    useSensor(PointerSensor),
  );

  const renderers = useMemo(() => createRenderers(), []);

  const handleBlobClick = (blob: Blob) => {
    setActiveBlobId(blob.id);
  };

  const { blobs, isLoading } = useBlobs({
    projectId,
    packageId,
    page: 'all',
    status: [
      BlobParsedStatus.UPLOADING,
      BlobParsedStatus.UPLOADED,
      BlobParsedStatus.UNPARSED,
      BlobParsedStatus.PARSED,
      BlobParsedStatus.PARSING_FAILED,
    ],
  });

  const allRows = useMemo(() => buildRows(blobs), [blobs]);

  const filteredRows = useMemo(() => {
    switch (activeTab) {
      case DocumentFilterTab.VERSION_A:
        return allRows.filter((row) => row.primaryBlob !== undefined);
      case DocumentFilterTab.VERSION_B:
        return allRows.filter((row) => row.secondaryBlob !== undefined);
      case DocumentFilterTab.COMMENT:
        return allRows.filter((row) => row.commentBlob !== undefined);
      default:
        return allRows;
    }
  }, [allRows, activeTab]);

  const navigableBlobs = useMemo(
    () =>
      filteredRows
        .map((row) => row.primaryBlob ?? row.secondaryBlob ?? row.commentBlob)
        .filter((blob): blob is Blob => blob !== undefined),
    [filteredRows],
  );
  const activeBlobIndex = navigableBlobs.findIndex((blob) => blob.id === activeBlobId);
  const activeBlob = activeBlobIndex >= 0 ? navigableBlobs[activeBlobIndex] : null;

  const tabs: { key: DocumentFilterTab; label: string; disabled?: boolean }[] = [
    { key: DocumentFilterTab.ALL, label: t('version-compare.tab-all') },
    { key: DocumentFilterTab.VERSION_A, label: t('version-compare.tab-version-a') },
    { key: DocumentFilterTab.VERSION_B, label: t('version-compare.tab-version-b') },
    { key: DocumentFilterTab.COMMENT, label: t('version-compare.tab-comment'), disabled: true },
  ];

  const versionABlobs = useMemo(
    () =>
      blobs.filter(
        (blob) => blob.metadata?.versionCompareRole === VersionCompareRole.PRIMARY_VERSION,
      ),
    [blobs],
  );
  const versionBBlobs = useMemo(
    () =>
      blobs.filter(
        (blob) => blob.metadata?.versionCompareRole === VersionCompareRole.SECONDARY_VERSION,
      ),
    [blobs],
  );
  const commentBlobs = useMemo(
    () =>
      blobs.filter(
        (blob) => blob.metadata?.versionCompareRole === VersionCompareRole.COMMENT_VERSION,
      ),
    [blobs],
  );

  const versionADownload = useDownloadFilesAsZip('version-a');
  const versionBDownload = useDownloadFilesAsZip('version-b');
  const commentDownload = useDownloadFilesAsZip('comment-register');

  const downloadButtons = [
    {
      label: t('version-compare.download-version-a'),
      blobs: versionABlobs,
      download: versionADownload,
    },
    {
      label: t('version-compare.download-version-b'),
      blobs: versionBBlobs,
      download: versionBDownload,
    },
    {
      label: t('version-compare.download-comment'),
      blobs: commentBlobs,
      download: commentDownload,
      disabled: true,
    },
  ];

  if (isLoading) {
    return <LoadingArea title={t('document-list.loading')} />;
  }

  return (
    <DndContext sensors={sensors} modifiers={[snapCenterToCursor, restrictToParentElement]}>
      <div className="self-start w-full px-6 py-4">
        <div className="mb-4 flex gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              disabled={tab.disabled}
              onClick={() => setActiveTab(tab.key)}
              className={
                activeTab === tab.key
                  ? 'rounded-full bg-day-neutral-dark px-3 py-1 text-xs font-medium text-white transition-colors'
                  : 'rounded-full bg-day-light-3 px-3 py-1 text-xs font-medium text-day-neutral-subtle transition-colors hover:bg-day-light-4 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-day-light-3'
              }
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
          {downloadButtons.map(({ label, blobs: typeBlobs, download, disabled }) => (
            <Button
              key={label}
              rounded
              secondary
              disabled={disabled || typeBlobs.length === 0 || download.isDownloading}
              onClick={() => download.downloadFileZip(typeBlobs.map(convertBlobToDocument))}
              icon={
                download.isDownloading
                  ? () => <Spinner size="xsmall" />
                  : () => <MdOutlineFileDownload />
              }
              className="text-sm font-bold min-h-[36px] whitespace-normal"
            >
              {label}
            </Button>
          ))}
        </div>

        <div className="overflow-hidden rounded border border-gray-200 bg-white shadow-sm">
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-[35%]" />
              <col className="w-[20%]" />
              <col className="w-[25%]" />
              <col className="w-[20%]" />
            </colgroup>
            <thead className="bg-day-light-3">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {t('version-compare.documents-column-document')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {t('version-compare.documents-column-version-a')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {t('version-compare.documents-column-version-b')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {t('version-compare.documents-column-comment')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-sm text-gray-500">
                    {t('version-compare.no-documents')}
                  </td>
                </tr>
              )}
              {filteredRows.map((row) => {
                const blob = row.primaryBlob ?? row.secondaryBlob ?? row.commentBlob;
                return (
                  <tr
                    key={blob?.id}
                    className="cursor-pointer hover:bg-day-light-4"
                    onClick={() => blob && handleBlobClick(blob)}
                  >
                    <td className="truncate px-6 py-4 text-sm font-semibold text-gray-900">
                      {row.name}
                    </td>
                    <td className="truncate px-6 py-4 text-sm text-gray-700">
                      {row.primaryBlob ? (
                        row.primaryBlob.name
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="truncate px-6 py-4 text-sm text-gray-700">
                      {row.secondaryBlob ? (
                        row.secondaryBlob.name
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="truncate px-6 py-4 text-sm text-gray-700">
                      {row.commentBlob ? (
                        row.commentBlob.name
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {activeBlob && (
        <DocumentViewer
          open={activeBlobIndex >= 0}
          onOpenChange={(details) => {
            if (!details.open) setActiveBlobId(null);
          }}
          src={resolveDocumentSrc(activeBlob)}
          fileType={resolveDocumentFileType(activeBlob)}
          fileName={activeBlob.name}
          title={activeBlob.name}
          showSidebar={false}
          showNavigationActions={navigableBlobs.length > 1}
          previousDisabled={activeBlobIndex <= 0}
          nextDisabled={activeBlobIndex >= navigableBlobs.length - 1}
          onPrevious={() => setActiveBlobId(navigableBlobs[activeBlobIndex - 1]?.id ?? null)}
          onNext={() => setActiveBlobId(navigableBlobs[activeBlobIndex + 1]?.id ?? null)}
          renderers={renderers}
        />
      )}
    </DndContext>
  );
};

export const VersionCompareDocumentsPage: FC = () => {
  const { t } = useTranslation();

  return (
    <WorkspaceProvider>
      <WorkspaceLayout>
        <Breadcrumb label={t('servicetabs.documents')} />
        <VersionCompareDocumentsContent />
      </WorkspaceLayout>
    </WorkspaceProvider>
  );
};
